import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

const SESSION_GRANTS_BASE = `${environment.newApiBackend}/api/v2/auth/profile/session-grants`;
const HEADER_FB_TOKEN = 'fb-token';

export interface MintSessionGrantResponse {
  grantId: string;
  sessionGrant: string;
  expiresAt: string;
}

export interface ExchangeSessionGrantResponse {
  customToken: string;
  expiresInSec: number;
}

/**
 * Dashboard-only bridge for VDS-3308 session grants.
 * Does not change the shared mobile login contract — mint is called after login OK.
 * Session identity is the opaque grant plaintext (another device = another secret).
 */
@Injectable({ providedIn: 'root' })
export class SessionGrantService {
  constructor(private readonly http: HttpClient) {}

  async mint(
    fbToken: string,
    previousSessionGrant?: string,
  ): Promise<MintSessionGrantResponse | null> {
    if (!fbToken) {
      return null;
    }
    try {
      const headers = new HttpHeaders().set(HEADER_FB_TOKEN, fbToken);
      const body = previousSessionGrant
        ? { previousSessionGrant }
        : {};
      const res: { data?: MintSessionGrantResponse } & MintSessionGrantResponse =
        await this.http
          .post<
            { data?: MintSessionGrantResponse } & MintSessionGrantResponse
          >(SESSION_GRANTS_BASE, body, { headers })
          .toPromise();
      const data = res?.data ?? res;
      if (!data?.sessionGrant) {
        return null;
      }
      return data;
    } catch (error) {
      console.error('SessionGrantService.mint failed:', error);
      return null;
    }
  }

  async exchange(sessionGrant: string): Promise<ExchangeSessionGrantResponse | null> {
    if (!sessionGrant) {
      return null;
    }
    try {
      const res: {
        data?: ExchangeSessionGrantResponse;
      } & ExchangeSessionGrantResponse = await this.http
        .post<
          { data?: ExchangeSessionGrantResponse } & ExchangeSessionGrantResponse
        >(`${SESSION_GRANTS_BASE}/exchange`, { sessionGrant })
        .toPromise();
      const data = res?.data ?? res;
      if (!data?.customToken) {
        return null;
      }
      return data;
    } catch (error) {
      console.error('SessionGrantService.exchange failed:', error);
      return null;
    }
  }

  async revokeCurrent(sessionGrant: string): Promise<void> {
    if (!sessionGrant) {
      return;
    }
    try {
      await this.http
        .request('delete', `${SESSION_GRANTS_BASE}/current`, {
          body: { sessionGrant },
        })
        .toPromise();
    } catch (error) {
      console.error('SessionGrantService.revokeCurrent failed:', error);
    }
  }

  async revokeMany(sessionGrants: string[]): Promise<void> {
    const grants = (sessionGrants ?? []).filter((g) => !!g);
    if (grants.length === 0) {
      return;
    }
    try {
      await this.http
        .request('delete', SESSION_GRANTS_BASE, {
          body: { sessionGrants: grants },
        })
        .toPromise();
    } catch (error) {
      console.error('SessionGrantService.revokeMany failed:', error);
    }
  }
}
